import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api';
import { Chip, Fold, Modal, checkboxClasses, primaryButton, secondaryButton } from '../../components/ui';
import { blockedText } from '../../lib/equalize';
import { plural } from '../../lib/text';

const heading = 'text-xs font-semibold uppercase tracking-wide text-slate-500';
const bulkLink = 'font-semibold text-zafiro-600 hover:underline disabled:hover:no-underline disabled:text-slate-400 disabled:cursor-not-allowed';

// Primer paso de igualar: muestra qué se publicaría en el destino, sin cambiar nada. Recién "Sí, publicar" lo hace,
// y manda lo que quedó tildado acá: el servidor publica esos updates y ningún otro.
// Tiene las mismas tres partes que "Confirmar liberación": qué se publica, qué no y por qué, y el total.
// `directions` son uno o dos sentidos ({ source, target }): con los dos ("Igualar los dos") cada uno tiene su lista
// y se confirman juntos. `onConfirm` recibe [{ source, target, filenames }] y devuelve lo que no se pudo publicar
// si el servidor rechazó el pedido: queda a la vista en el cartel.
export default function EqualizeDialog({ version, directions, onConfirm, onCancel }) {
    const [working, setWorking] = useState(false);
    const [rejected, setRejected] = useState(null);
    const keyOf = ({ source, target }) => `${source}>${target}`;
    const keys = directions.map(keyOf).join(',');
    // La vista previa de cada sentido: ausente mientras se pide y null si no se pudo.
    const [previews, setPreviews] = useState({});
    // Arrancan todos tildados; acá queda, por sentido, lo que el usuario destildó para dejarlo afuera.
    const [excluded, setExcluded] = useState({});
    useEffect(() => {
        let current = true;
        directions.forEach(direction => apiClient.get('/api/compare/preview', { params: { version, ...direction } })
            .then(res => { if (current) setPreviews(prev => ({ ...prev, [keyOf(direction)]: res.data })); })
            .catch(() => { if (current) setPreviews(prev => ({ ...prev, [keyOf(direction)]: null })); }));
        return () => { current = false; };
    }, [version, keys]); // eslint-disable-line react-hooks/exhaustive-deps

    const plans = directions.map(direction => {
        const key = keyOf(direction);
        return { ...direction, key, preview: previews[key], ...choose(previews[key], excluded[key] || []) };
    });
    const ready = plans.every(plan => plan.preview !== undefined);
    const chosen = plans.reduce((sum, plan) => sum + plan.chosen.length, 0);
    const offered = plans.reduce((sum, plan) => sum + plan.names.length, 0);
    const single = directions.length === 1;
    const [first] = directions;
    const confirm = async () => {
        setWorking(true);
        const parts = plans.filter(plan => plan.chosen.length > 0).map(({ source, target, chosen: filenames }) => ({ source, target, filenames }));
        setRejected(await onConfirm(parts) || null);
        setWorking(false);
    };
    // Sin saber qué se publicaría, o sin nada tildado, el botón no promete ninguna cantidad.
    const label = working ? 'Publicando…' : chosen > 0 ? (single ? `Sí, publicar ${chosen} en ${first.target}` : `Sí, publicar ${chosen}`) : 'Publicar';
    // Sin nada para publicar no hay nada que confirmar ni que cancelar: el cartel sólo se cierra.
    // Una vista previa que falló no es "nada": el botón queda gris, sin cantidad.
    const nothing = plans.every(plan => plan.preview) && offered === 0;
    return (
        <Modal title={single ? `Igualar ${first.target} con ${first.source}` : `Igualar ${first.source} y ${first.target}`} onClose={onCancel} footer={nothing
            ? <button type="button" onClick={onCancel} className={secondaryButton}>Cerrar</button>
            : <>
                <button type="button" onClick={onCancel} className={secondaryButton}>Cancelar</button>
                <button type="button" onClick={confirm} disabled={chosen === 0 || working} className={primaryButton}>{label}</button>
            </>}>
            {plans.map(plan => (
                <DirectionPreview key={plan.key} version={version} plan={plan} titled={!single} working={working}
                    setExcluded={(update) => setExcluded(prev => ({ ...prev, [plan.key]: update(prev[plan.key] || []) }))} />
            ))}
            {ready && !nothing && <p className="p-3 rounded-md bg-slate-50"><strong>Total: {plural(chosen, 'publicación', 'publicaciones')}.</strong></p>}
            {rejected && (
                <div role="alert" aria-label="Lo que no se pudo publicar" className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
                    <p className="font-semibold">{rejected.message}</p>
                    {rejected.details.length > 0 && <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto font-mono text-xs">{rejected.details.map((text, i) => <li key={i} className="break-words">{text}</li>)}</ul>}
                </div>
            )}
        </Modal>
    );
}

// Lo que se publica de un sentido: lo tildado cuyos prerrequisitos también van. Dejar afuera uno deja afuera a quien
// lo necesita, que es lo mismo que hace el servidor al confirmar.
function choose(preview, excluded) {
    const names = preview?.to_publish || [], requires = preview?.requires || {};
    const going = new Set(names.filter(name => !excluded.includes(name)));
    for (let changed = true; changed;) {
        changed = false;
        going.forEach(name => { if ((requires[name] || []).some(needed => !going.has(needed))) { going.delete(name); changed = true; } });
    }
    return { names, requires, excluded, going, chosen: names.filter(name => going.has(name)) };
}

// Un sentido del igualado: lo que se tilda y lo que no se publica. Con dos sentidos lleva título.
function DirectionPreview({ version, plan, titled, working, setExcluded }) {
    const { source, target, preview, names, requires, excluded, going, chosen } = plan;
    const outdated = (preview?.outdated || []).filter(name => going.has(name));
    const toggle = (name) => setExcluded(prev => (prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]));
    const nothing = Boolean(preview) && names.length === 0;
    const blocked = preview?.blocked || [], owned = preview?.skipped_owner || [], ignored = preview?.ignored || [];
    const owners = [...new Set(owned.map(item => item.owner))];
    return (
        <section aria-label={titled ? `De ${source} a ${target}` : undefined} className={titled ? 'space-y-3 pb-4 border-b border-slate-200 last-of-type:border-b-0' : 'contents'}>
            {titled && <h3 className="font-semibold text-slate-900">De {source} a {target}</h3>}
            {preview === undefined && <p role="status">Calculando qué se publicaría…</p>}
            {preview === null && <p role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">No se pudo calcular qué se publicaría. No se cambió nada.</p>}
            {preview && <>
                <p>{nothing ? <>No hay nada para publicar en <strong>{target}</strong>.</>
                    : chosen.length === 0 ? <>Todavía no se publicó nada. No dejaste ningún update tildado para publicar en <strong>{target}</strong>.</>
                    : <>Todavía no se publicó nada. Si confirmás, se {chosen.length === 1 ? 'publica' : 'publican'} <strong>{plural(chosen.length, 'update', 'updates')}</strong> en <strong>{target}</strong>, versión <span className="font-mono">{version}</span>: {chosen.length === 1 ? 'el que' : 'los que'} <strong>{source}</strong> tiene y a {target} le {chosen.length === 1 ? 'falta' : 'faltan'}.</>}</p>

                {names.length > 0 && (
                    <section aria-label="Se publica">
                        <div className="flex items-center gap-3 mb-1.5">
                            <h3 className={heading}>Se publica · {chosen.length === names.length ? names.length : `${chosen.length} de ${names.length}`}</h3>
                            {names.length > 1 && <span className="ml-auto flex gap-3 text-xs">
                                <button type="button" disabled={excluded.length === 0} onClick={() => setExcluded(() => [])} className={bulkLink}>Tildar todos</button>
                                <button type="button" disabled={chosen.length === 0} onClick={() => setExcluded(() => names)} className={bulkLink}>Destildar todos</button>
                            </span>}
                        </div>
                        <ul aria-label={`Updates que se publicarían en ${target}`} className="border border-zafiro-200 bg-zafiro-50 rounded-lg divide-y divide-zafiro-100 max-h-56 overflow-y-auto">
                            {names.map(name => {
                                // Un update que necesita uno que se dejó afuera tampoco va: se ve destildado y dice por qué.
                                const missing = !excluded.includes(name) && (requires[name] || []).find(needed => !going.has(needed));
                                return (
                                    <li key={name} className={`flex items-center gap-2 px-3 py-1.5 ${going.has(name) ? '' : 'bg-white text-slate-500'}`}>
                                        <input type="checkbox" aria-label={`Publicar ${name}`} className={`${checkboxClasses} flex-shrink-0`} checked={going.has(name)} disabled={working || Boolean(missing)} onChange={() => toggle(name)} />
                                        <span className="flex-grow min-w-0 font-mono text-[13px] font-medium truncate" title={name}>{name}</span>
                                        {missing && <span className="flex-shrink-0 text-xs" title={`Necesita ${missing}, que quedó afuera.`}>necesita uno que quedó afuera</span>}
                                        {going.has(name) && preview.outdated?.includes(name) && <Chip tone="amber" title={`En ${source} está desactualizado y llega igual: después hay que republicarlo.`}>llega desactualizado</Chip>}
                                    </li>
                                );
                            })}
                        </ul>
                        {outdated.length > 0 && (
                            <p role="note" className="mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                                {outdated.length === 1 ? '1 llega desactualizado' : `${outdated.length} llegan desactualizados`}, igual que en {source}. Después hay que {outdated.length === 1 ? 'republicarlo' : 'republicarlos'}.
                            </p>
                        )}
                    </section>
                )}

                {blocked.length + owned.length + ignored.length > 0 && (
                    <section aria-label="No se publica" className="space-y-2">
                        <h3 className={heading}>No se publica · {blocked.length + owned.length + ignored.length}</h3>
                        {/* Lo que no cumple las reglas de publicar va en rojo y a la vista, como las fallas del cartel de Updates. */}
                        {blocked.length > 0 && (
                            <div role="alert" className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
                                <p className="font-semibold">{blocked.length === 1 ? '1 no se publica:' : `${blocked.length} no se publican:`}</p>
                                <ul aria-label={`Updates que no se publican en ${target}`} className="mt-1 space-y-1 max-h-40 overflow-y-auto">
                                    {blocked.map(item => <li key={item.name}><span className="font-mono text-[13px] font-medium break-all">{item.name}</span>: {blockedText(item, source, target)}</li>)}
                                </ul>
                            </div>
                        )}
                        {/* Lo esperado va en gris y plegado: cuántos y por qué, y los nombres detrás de "ver detalle". */}
                        {owned.length > 0 && (
                            <p className="text-xs text-slate-500">
                                {owned.length === 1 ? '1 no se publica: es' : `${owned.length} no se publican: son`} de {owners.length === 1 ? owners[0] : 'otros clientes'}.{' '}
                                <Fold about="lo que es de otro cliente">
                                    {owned.map(item => <span key={item.name} className="block font-mono break-all">{item.name}{owners.length > 1 && <span className="font-sans"> · es de {item.owner}</span>}</span>)}
                                </Fold>
                            </p>
                        )}
                        {ignored.length > 0 && (
                            <p className="text-xs text-slate-500">
                                {ignored.length === 1 ? '1 queda ignorado' : `${ignored.length} quedan ignorados`} para {target}.{' '}
                                <Fold about={`lo que ${target} tiene ignorado`}>
                                    {ignored.map(name => <span key={name} className="block font-mono break-all">{name}</span>)}
                                </Fold>
                            </p>
                        )}
                    </section>
                )}

            </>}
        </section>
    );
}
